import React, {forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useId} from "react";
import {scopeSvg} from "./scopeSvg";
import {decodeSvgFromDataUrl} from "./bg-utils";

const MIN_SCALE = 0.1;
const MAX_SCALE = 8;
const PINCH_SPEED = 0.0012; // wheel delta -> scale change (Chrome/Edge)

// Helper function to determine if src is a video
const isVideoSource = (src) => {
  if (!src) return false;

  // Check if it's a data URL (base64)
  if (src.startsWith("data:")) {
    const mimeMatch = src.match(/^data:([^;]+);/);
    if (mimeMatch && mimeMatch[1]) {
      const mimeType = mimeMatch[1].toLowerCase();
      return mimeType.startsWith("video/");
    }
    return false;
  }

  // Check by extension for regular URLs
  const videoExtensions = [".mp4", ".webm", ".ogv", ".mov", ".m4v", ".mkv"];
  const srcLower = src.toLowerCase();
  return videoExtensions.some((ext) => srcLower.includes(ext));
};

const isSvgSource = (src) => {
  if (typeof src !== "string" || !src) return false;
  if (src.startsWith("data:image/svg")) return true;
  const trimmed = src.trim();
  if (trimmed.startsWith("<svg") || trimmed.startsWith("<?xml")) return true;
  return /\.svg(\?|#|$)/i.test(src);
};

const BiggerPicture = forwardRef(function BiggerPicture(
  {
    src,
    wrapperClassName,
    imgClassName,
    imgStyle,
    wrapperStyle,
    x,
    y,
    scale,
    flip,
    rotate,
    onTransform,
    minScale = MIN_SCALE,
    maxScale = MAX_SCALE,
    pinchSpeed = PINCH_SPEED,
    wheelZoom = true,
    wheelSpeed = PINCH_SPEED,
    // Called once the image (or the first video frame) is ready to show.
    onLoad,
  },
  ref
) {
  const wrapperRef = useRef(null);
  const moverRef = useRef(null);

  const dragRef = useRef({active: false, sx: 0, sy: 0, x0: 0, y0: 0});
  const pointerIdRef = useRef(null);
  const draggingRef = useRef(false);

  const xRef = useRef(0);
  const yRef = useRef(0);
  const scaleRef = useRef(1);
  const baseGestureScaleRef = useRef(1);

  const onTransformRef = useRef(onTransform);
  useEffect(() => {
    onTransformRef.current = onTransform;
  }, [onTransform]);

  useEffect(() => {
    xRef.current = x;
  }, [x]);
  useEffect(() => {
    yRef.current = y;
  }, [y]);
  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);

  const translateTransform = `translate(${x}px, ${y}px) scale(${scale}, ${scale})`;
  const sx = flip.x ? -1 : 1;
  const sy = flip.y ? -1 : 1;
  const rotateScaleTransform = `rotate(${rotate}deg) scale(${sx}, ${sy})`;

  const isVideo = isVideoSource(src);
  const isSvg = !isVideo && isSvgSource(src);
  const svgScope = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const svgMarkup = useMemo(() => {
    const markup = isSvg ? decodeSvgFromDataUrl(src) : null;
    return markup ? scopeSvg(markup, `svg-${svgScope}`) : null;
  }, [isSvg, src, svgScope]);
  const svgWrapperRef = useRef(null);

  // Inline the sanitized SVG markup into the DOM so its <linearGradient>,
  // <radialGradient>, <stop>, etc. are real, query-able nodes. The visible
  // element of the moverRef tree must be a single child that fits its own
  // content (BiggerPicture's zoom math reads `moverRef.children[0]`), so we
  // inject into a wrapper div and let the SVG inside drive its size.
  useEffect(() => {
    if (!isSvg) return;
    const el = svgWrapperRef.current;
    if (!el) return;
    if (svgMarkup) {
      el.innerHTML = svgMarkup;
    } else {
      // Decoder rejected the SVG (parse error / non-SVG payload). Fall back
      // to the hidden <img>, which the browser will render in sandbox mode.
      el.innerHTML = "";
    }
  }, [isSvg, svgMarkup]);

  function removeGlobals() {
    window.removeEventListener("pointerup", stopDrag, true);
    window.removeEventListener("pointercancel", stopDrag, true);
    window.removeEventListener("blur", stopDrag, true);
  }
  function stopDrag() {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    dragRef.current.active = false;
    try {
      if (pointerIdRef.current != null) {
        wrapperRef.current?.releasePointerCapture?.(pointerIdRef.current);
      }
    } catch {
      // Ignore capture-release failures from stale pointer state.
    }
    pointerIdRef.current = null;
    removeGlobals();
  }

  function addGlobals() {
    window.addEventListener("pointerup", stopDrag, true);
    window.addEventListener("pointercancel", stopDrag, true);
    window.addEventListener("blur", stopDrag, true);
  }

  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    draggingRef.current = true;
    dragRef.current = {
      active: true,
      sx: e.clientX,
      sy: e.clientY,
      x0: xRef.current,
      y0: yRef.current,
    };
    pointerIdRef.current = e.pointerId;
    wrapperRef.current?.setPointerCapture?.(e.pointerId);
    addGlobals();
  };

  const onPointerMove = (e) => {
    if (!draggingRef.current) return;
    if (pointerIdRef.current != null && e.pointerId !== pointerIdRef.current) return;
    const {sx, sy, x0, y0} = dragRef.current;
    const nx = x0 + (e.clientX - sx) * 2;
    const ny = y0 + (e.clientY - sy) * 2;
    // use latest onTransform
    onTransformRef.current(nx, ny, scaleRef.current);
    xRef.current = nx;
    yRef.current = ny;
  };

  const onPointerUp = () => stopDrag();
  const onPointerCancel = () => stopDrag();
  const onLostPointerCapture = () => stopDrag();
  const onPointerLeave = (e) => {
    if (!e.buttons) stopDrag();
  };

  const zoomTo = (targetScale) => {
    const s0 = scaleRef.current;
    const s1 = Math.min(MAX_SCALE, Math.max(MIN_SCALE, targetScale));
    if (s1 === s0) return;

    const el = moverRef.current.children[0];
    const cx = el?.offsetWidth ? el.offsetWidth / 2 : 0;
    const cy = el?.offsetHeight ? el.offsetHeight / 2 : 0;

    const dx = (s0 - s1) * cx;
    const dy = (s0 - s1) * cy;

    const nx = xRef.current + dx;
    const ny = yRef.current + dy;

    // use latest onTransform
    onTransformRef.current(nx, ny, s1);

    scaleRef.current = s1;
    xRef.current = nx;
    yRef.current = ny;
  };

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const onWheelNative = (e) => {
      const isPinchWheel = e.ctrlKey || e.metaKey; // trackpad pinch in Chrome/Edge/FF
      if (!isPinchWheel && !wheelZoom) return;
      e.preventDefault();
      const dy = e.deltaY;
      const speed = isPinchWheel ? pinchSpeed : wheelSpeed;
      zoomTo(scaleRef.current + -dy * speed);
    };

    const onGestureStart = (e) => {
      e.preventDefault();
      baseGestureScaleRef.current = scaleRef.current;
    };
    const onGestureChange = (e) => {
      e.preventDefault();
      zoomTo(baseGestureScaleRef.current * e.scale);
    };
    const onGestureEnd = (e) => {
      e.preventDefault();
    };

    el.addEventListener("wheel", onWheelNative, {passive: false});
    el.addEventListener("gesturestart", onGestureStart, {passive: false});
    el.addEventListener("gesturechange", onGestureChange, {passive: false});
    el.addEventListener("gestureend", onGestureEnd, {passive: false});

    return () => {
      el.removeEventListener("wheel", onWheelNative);
      el.removeEventListener("gesturestart", onGestureStart);
      el.removeEventListener("gesturechange", onGestureChange);
      el.removeEventListener("gestureend", onGestureEnd);
    };
  }, [pinchSpeed, wheelZoom, wheelSpeed]);

  useImperativeHandle(ref, () => ({
    zoomIn: () => zoomTo(scaleRef.current + 0.1),
    zoomOut: () => zoomTo(scaleRef.current - 0.1),
  }));

  const mediaStyle = {
    display: "flex",
    pointerEvents: "none",
    willChange: "transform",
    flexWrap: "wrap",
    width: "fit-content",
    height: "fit-content",
    transform: rotateScaleTransform,
    transition: "transform 0.25s ease-out",
    transformOrigin: "center center",
    margin: 0,
    padding: 0,
    ...imgStyle,
  };

  return (
    <div
      ref={wrapperRef}
      className={wrapperClassName}
      style={{
        touchAction: "none",
        userSelect: "none",
        cursor: "grab",
        overflow: "hidden",
        position: "relative",
        ...wrapperStyle,
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onLostPointerCapture={onLostPointerCapture}
      onPointerLeave={onPointerLeave}
    >
      {/* Outer: translate + uniform scale (origin 0 0) */}
      <div
        ref={moverRef}
        style={{
          willChange: "transform",
          display: "flex",
          pointerEvents: "none",
          flexWrap: "wrap",
          width: "fit-content",
          height: "fit-content",
          margin: 0,
          padding: 0,
          transform: translateTransform,
          transition: draggingRef.current ? "none" : "transform 0.25s ease-out",
          transformOrigin: "0% 0%", // keep behavior/back-compat
        }}
      >
        {/* Inner: rotate + flip (origin center) */}
        {isVideo ? (
          <video
            src={src}
            alt="background"
            className={imgClassName}
            style={mediaStyle}
            draggable={false}
            crossOrigin="anonymous"
            controls={false}
            autoPlay
            loop
            muted
            playsInline
            onLoadedData={onLoad}
          />
        ) : isSvg && svgMarkup ? (
          // Inline-SVG path. moverRef.children[0] is this outer <span> —
          // its fit-content sizing comes from the SVG sub-tree inside
          // svgWrapperRef, which is mutated via innerHTML (not by React)
          // to sidestep reconciliation of the parsed SVG nodes. Both
          // wrappers are <span> on purpose: BgTransformer's CSS forces
          // `.bg-transformer .image-wrapper div { width:100% !important }`,
          // which would crush a div wrapper to the full template size and
          // break the zoom math (`el.offsetWidth/2` reads the inner el).
          //
          // The off-screen <img alt="background"> sibling keeps two
          // existing consumers happy:
          //   (a) the server renderer's readiness probe
          //       `.bg-transformer img[alt='background']` still resolves
          //       once the SVG data URL decodes,
          //   (b) smart-crop's computed-style read of `filter`
          //       (brightness/blur) still has a target.
          // Keeping innerHTML-managed content in its own ref'd span means
          // the React-managed <img> sibling never gets clobbered.
          <span
            className={imgClassName}
            style={{...mediaStyle, position: "relative"}}
            data-bg-inline-svg=""
          >
            <span ref={svgWrapperRef} style={{display: "flex"}} />
            <img
              src={src}
              alt="background"
              aria-hidden="true"
              draggable={false}
              crossOrigin="anonymous"
              onLoad={onLoad}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: 1,
                height: 1,
                opacity: 0,
                pointerEvents: "none",
              }}
            />
          </span>
        ) : (
          <img
            src={src}
            alt="background"
            className={imgClassName}
            style={mediaStyle}
            draggable={false}
            crossOrigin="anonymous"
            onLoad={onLoad}
          />
        )}
      </div>
    </div>
  );
});

export default BiggerPicture;
